import { useEffect } from 'react';
import { Button, Modal, Spinner } from 'flowbite-react';
import { HiCheckCircle, HiXCircle } from 'react-icons/hi';

export const PROGRESS_STATUS = {
    PROCESSING: 'processing',
    SUCCESS: 'success',
    ERROR: 'error',
};

const AUTO_CLOSE_MS = 120 * 1000;

/**
 * Status-driven progress dialog.
 *
 * - PROCESSING: shows a spinner + Cancel button.
 * - SUCCESS / ERROR: shows the result, then auto-closes after 3s.
 *
 * @param {boolean} open
 * @param {string}  status   one of PROGRESS_STATUS
 * @param {string}  title
 * @param {string}  processingMessage
 * @param {string}  successMessage
 * @param {string}  errorMessage
 * @param {() => void} onCancel  called when the user cancels while processing
 * @param {() => void} onClose   called on auto-close (or backdrop close once done)
 */
export default function ProgressDialog({
    open,
    status,
    title,
    processingMessage = 'Processing...',
    successMessage = 'Done!',
    errorMessage = 'Something went wrong.',
    onCancel,
    onClose,
    closeAfter = AUTO_CLOSE_MS
}) {
    const isProcessing = status === PROGRESS_STATUS.PROCESSING;
    const isDone = status === PROGRESS_STATUS.SUCCESS || status === PROGRESS_STATUS.ERROR;

    // Auto-close 3s after completion.
    useEffect(() => {
        if (!open || !isDone) return undefined;
        const timer = setTimeout(() => {
            if (onClose) onClose();
        }, closeAfter);
        return () => clearTimeout(timer);
    }, [open, isDone, onClose]);

    // While processing, ignore backdrop close; once done, allow it.
    const handleBackdropClose = () => {
        if (isProcessing) return;
        if (onClose) onClose();
    };

    return (
        <Modal show={open} size="md" popup onClose={handleBackdropClose} dismissible={isDone}>
            <Modal.Header>{title}</Modal.Header>
            <Modal.Body>
                <div className="text-center py-2">
                    {isProcessing && (
                        <>
                            <Spinner size="xl" className="mx-auto mb-4" />
                            <h3 className="mb-5 text-lg font-normal text-gray-500">
                                {processingMessage}
                            </h3>
                            <div className="flex justify-center gap-4">
                                <Button color="gray" onClick={onCancel}>
                                    Cancel
                                </Button>
                            </div>
                        </>
                    )}

                    {status === PROGRESS_STATUS.SUCCESS && (
                        <>
                            <HiCheckCircle className="mx-auto mb-4 h-14 w-14 text-green-500" />
                            <h3 className="mb-2 text-lg font-normal text-gray-600">
                                {successMessage}
                            </h3>
                            <p className="text-sm text-gray-400">This dialog will close automatically.</p>
                        </>
                    )}

                    {status === PROGRESS_STATUS.ERROR && (
                        <>
                            <HiXCircle className="mx-auto mb-4 h-14 w-14 text-red-500" />
                            <h3 className="mb-2 text-lg font-normal text-gray-600">
                                {errorMessage}
                            </h3>
                            <p className="text-sm text-gray-400">This dialog will close automatically.</p>
                        </>
                    )}
                </div>
            </Modal.Body>
        </Modal>
    );
}
